// import React, { useState, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import { useWebsitePackages } from "../hooks/useWebsitePackages";
// import styles from "./PackagesListPage1.module.css";
// import { FaSearch, FaMapMarkerAlt, FaClock } from "react-icons/fa";

// const PackagesListPage1 = ({ companyId, mode, onNavigate, templateId }) => {
//   const navigate = useNavigate();
//   const [searchTerm, setSearchTerm] = useState("");

//   // 1. Fetch data (uses your existing hook)
//   // We pass 'mode' so it still shows Demo Data in the Builder if the agent has no packages
//   const { data: packages = [], isLoading } = useWebsitePackages(companyId, "Holiday", mode);

//   // 2. Search Logic (Filters by Package Name or Destination)
//   const filteredPackages = useMemo(() => {
//     return packages.filter((pkg) =>
//       pkg.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       pkg.destination?.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//   }, [searchTerm, packages]);

//   const handleViewDetails = (pkgId) => {
//     if (mode === "builder") {
//       onNavigate({ name: "package-details", params: { packageId: pkgId } });
//     } else if (mode === "preview") {
//       navigate(`/template-preview/${templateId}/package/${pkgId}`);
//     } else {
//       navigate(`/package/${pkgId}`);
//     }
//   };

//   if (isLoading) return <div className={styles.loader}>Loading our best deals...</div>;

//   return (
//     <div className={styles.container}>
//       {/* Search Section */}
//       <section className={styles.searchSection}>
//         <h1 className={styles.title}>Find Your Dream Package</h1>
//         <div className={styles.searchBarWrapper}>
//           <FaSearch className={styles.searchIcon} />
//           <input
//             type="text"
//             placeholder="Search by destination or package name..."
//             className={styles.searchInput}
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>
//         <p className={styles.resultsCount}>
//           Showing {filteredPackages.length} packages
//         </p>
//       </section>

//       {/* Grid Section */}
//       <div className={styles.grid}>
//         {filteredPackages.length > 0 ? (
//           filteredPackages.map((pkg) => (
//             <div key={pkg.packageId} className={styles.card}>
//               <div className={styles.imageBox}>
//                 <img src={pkg.imageSrc} alt={pkg.packageName} className={styles.image} />
//                 <span className={styles.priceBadge}>
//                   {pkg.currency} {pkg.price}
//                 </span>
//               </div>
//               <div className={styles.content}>
//                 <h3 className={styles.cardTitle}>{pkg.packageName}</h3>
//                 <div className={styles.meta}>
//                    <span><FaMapMarkerAlt /> {pkg.destination || "Global"}</span>
//                    <span><FaClock /> {pkg.duration}</span>
//                 </div>
//                 <button 
//                   onClick={() => handleViewDetails(pkg.packageId)} 
//                   className={styles.button}
//                 >
//                   View Full Details
//                 </button>
//               </div>
//             </div>
//           ))
//         ) : (
//           <div className={styles.noResults}>
//             <h3>No packages found matching "{searchTerm}"</h3>
//             <button onClick={() => setSearchTerm("")} className={styles.clearBtn}>Clear Search</button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default PackagesListPage1;

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWebsitePackages } from "../hooks/useWebsitePackages";
import styles from "./PackagesListPage1.module.css";
import { FaSearch, FaMapMarkerAlt, FaClock } from "react-icons/fa";

// Fallback images
const fallbackImages = [
  "/assets/images/default-package.jpg",
  "/assets/images/default-package2.png",
  "/assets/images/default-package3.png",
  "/assets/images/default-package4.png",
  "/assets/images/default-package5.png",
  "/assets/images/default-package6.jpg"
];

const PackagesListPage1 = ({ companyId, mode, onNavigate, templateId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch packages
  const { data: packages = [], isLoading } = useWebsitePackages(
    companyId,
    "Holiday",
    mode
  );

  // Search logic
  const filteredPackages = useMemo(() => {
    return packages.filter(
      (pkg) =>
        pkg.packageName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.destination?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, packages]);

  const handleViewDetails = (pkgId) => {
    if (mode === "builder") {
      onNavigate({ name: "package-details", params: { packageId: pkgId } });
    } else if (mode === "preview") {
      navigate(`/template-preview/${templateId}/package/${pkgId}`);
    } else {
      navigate(`/package/${pkgId}`);
    }
  };

  if (isLoading)
    return <div className={styles.loader}>Loading our best deals...</div>;

  return (
    <div className={styles.container}>
      {/* Search Section */}
      <section className={styles.searchSection}>
        <h1 className={styles.title}>Find Your Dream Package</h1>

        <div className={styles.searchBarWrapper}>
          <FaSearch className={styles.searchIcon} />

          <input
            type="text"
            placeholder="Search by destination or package name..."
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <p className={styles.resultsCount}>
          Showing {filteredPackages.length} packages
        </p>
      </section>

      {/* Packages Grid */}
      <div className={styles.grid}>
        {filteredPackages.length > 0 ? (
          filteredPackages.map((pkg, index) => {
            const fallbackImage =
              fallbackImages[index % fallbackImages.length];

            const imageUrl =
              pkg?.imageSrc && pkg.imageSrc.trim() !== ""
                ? pkg.imageSrc
                : fallbackImage;

            return (
              <div key={pkg.packageId} className={styles.card}>
                <div className={styles.imageBox}>
                  <img
                    src={imageUrl}
                    alt={pkg.packageName}
                    className={styles.image}
                    onError={(e) => {
                      e.target.src = fallbackImage;
                    }}
                  />

                  <span className={styles.priceBadge}>
                    {pkg.currency} {pkg.price}
                  </span>
                </div>

                <div className={styles.content}>
                  <h3 className={styles.cardTitle}>{pkg.packageName}</h3>

                  <div className={styles.meta}>
                    <span>
                      <FaMapMarkerAlt /> {pkg.destination || "Global"}
                    </span>

                    <span>
                      <FaClock /> {pkg.duration}
                    </span>
                  </div>

                  <button
                    onClick={() => handleViewDetails(pkg.packageId)}
                    className={styles.button}
                  >
                    View Full Details
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.noResults}>
            <h3>No packages found matching "{searchTerm}"</h3>

            <button
              onClick={() => setSearchTerm("")}
              className={styles.clearBtn}
            >
              Clear Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackagesListPage1;